import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, PhoneCall, GitCompare } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { DOMAIN_AUTO, TICK_MUTED } from "@/lib/chart";
import { http } from "@/lib/data";
import { pct, tone, shortCat, navDate } from "@/lib/format";
import { useLead } from "@/components/layout/Layout";
import { SipWidget } from "@/components/SipWidget";
import { Glossary } from "@/components/Glossary";
import { crore, terFmt, amcSite } from "@/lib/amc";

const P = [["r1m", "1 Month"], ["r6m", "6 Months"], ["r1y", "1 Year"], ["r3y", "3Y CAGR"], ["r5y", "5Y CAGR"]];

export default function FundDetail() {
  const { code } = useParams();
  const openLead = useLead();
  const { data: f, isLoading, error } = useQuery({ queryKey: ["fund", code], queryFn: () => http.get(`/mf/fund/${code}`).then((r) => r.data), retry: (n, e) => e?.response?.status === 503 && n < 5, retryDelay: 4000 });
  if (isLoading) return <div className="max-w-7xl mx-auto p-10 text-slate-400">Loading live fund data…</div>;
  if (error || !f) return <div data-testid="fund-not-found" className="max-w-7xl mx-auto p-10">Fund not found. <Link to="/mutual-funds" className="text-[#059669] font-bold">Back to funds</Link></div>;

  return (
    <div data-testid="fund-detail-page">
      <section className="bg-[#1E293B] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
          <Link to="/mutual-funds" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white"><ArrowLeft className="w-4 h-4" />All funds</Link>
          <div className="mt-4 flex flex-wrap gap-2 text-xs font-bold">
            <span className="rounded-full bg-amber-300/15 text-amber-200 px-3 py-1">{f.asset_class}</span>
            <span className="rounded-full bg-white/10 px-3 py-1">{shortCat(f.category)}</span>
            <span className="rounded-full bg-white/10 px-3 py-1">{f.plan} · Growth</span>
          </div>
          <h1 data-testid="fund-name" className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mt-4 max-w-4xl">{f.name}</h1>
          <p className="text-slate-400 mt-2">{f.amc} · AMFI code {f.code}</p>
          <div className="mt-8 flex flex-wrap items-end gap-x-10 gap-y-4">
            <div><p className="text-xs text-slate-400">NAV ({navDate(f.nav_date)})</p><p data-testid="fund-nav" className="font-mono text-4xl font-bold">₹{f.nav.toFixed(4)}</p></div>
            {f.category_rank && <div><p className="text-xs text-slate-400">3Y rank in category</p><p data-testid="fund-rank" className="font-mono text-2xl font-bold text-amber-300">#{f.category_rank} <span className="text-base text-slate-400">of {f.category_size}</span></p></div>}
            <div><p className="text-xs text-slate-400">Expense ratio</p><p data-testid="fund-ter" className="font-mono text-2xl font-bold">{terFmt(f.ter)}</p></div>
            <div><p className="text-xs text-slate-400">Fund size (AUM)</p><p data-testid="fund-aum" className="font-mono text-2xl font-bold">{crore(f.aum_cr)}</p></div>
            <div className="flex flex-wrap gap-3 ml-auto">
              <a href={amcSite(f.amc)} target="_blank" rel="noopener noreferrer" data-testid="fund-amc-site" className="h-12 px-5 rounded-xl border border-white/20 font-bold inline-flex items-center gap-2 hover:bg-white/10">Invest on AMC site</a>
              <Link to={`/compare?type=mf&ids=${f.code}`} data-testid="fund-compare-button" className="h-12 px-5 rounded-xl border border-white/20 font-bold inline-flex items-center gap-2 hover:bg-white/10"><GitCompare className="w-4 h-4" />Compare</Link>
              <button data-testid="fund-advisor-button" onClick={() => openLead({ id: `mf-${f.code}`, company_name: f.name })} className="h-12 px-5 rounded-xl bg-[#10B981] hover:bg-[#059669] text-[#0F172A] font-bold inline-flex items-center gap-2"><PhoneCall className="w-4 h-4" />Talk to an Advisor</button>
            </div>
          </div>
        </div>
      </section>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-5 sm:p-6">
          <p className="font-display font-bold text-lg">NAV history (month-end, up to 5 years)</p>
          <div className="h-72 mt-4" data-testid="fund-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={f.series}>
                <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10B981" stopOpacity={0.35} /><stop offset="100%" stopColor="#10B981" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="date" tickFormatter={(d) => d.slice(0, 7)} tick={TICK_MUTED} minTickGap={40} />
                <YAxis domain={DOMAIN_AUTO} tick={TICK_MUTED} width={50} />
                <Tooltip formatter={(v) => [`₹${Number(v).toFixed(2)}`, "NAV"]} labelFormatter={navDate} />
                <Area type="monotone" dataKey="nav" stroke="#10B981" strokeWidth={2.5} fill="url(#g)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          {f.series.length < 6 && <p className="text-xs text-slate-500 mt-2">History is still syncing from AMFI — refresh in a minute for the full chart.</p>}
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6">
          <p className="font-display font-bold text-lg">Returns vs category average</p>
          <table data-testid="fund-returns-table" className="w-full mt-4 text-sm">
            <thead className="text-xs text-slate-500"><tr><th className="text-left py-2">Period</th><th className="text-right">Fund</th><th className="text-right">Category</th></tr></thead>
            <tbody>
              {P.map(([k, l]) => (
                <tr key={k} className="border-t border-slate-100">
                  <td className="py-3 font-semibold">{l}</td>
                  <td className={`py-3 text-right font-mono font-bold ${tone(f[k])}`}>{pct(f[k])}</td>
                  <td className={`py-3 text-right font-mono ${tone(f.category_avg?.[k])}`}>{pct(f.category_avg?.[k])}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-slate-500 mt-4">Computed from official AMFI NAVs. Past performance is not indicative of future returns. Read all scheme documents carefully.</p>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <SipWidget code={f.code} name={f.name} />
        <Glossary set={["Index Funds", "ETF", "Fund of Funds"].includes(f.asset_class) ? "index" : "mf"} />
      </div>
    </div>
  );
}
